import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'


import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { readProductsScope } from '@features/landing-page-builder'
import { useAddressStore } from '@features/shipping-methods'

import { CategoriesBranch } from '@widgets/categories-branch'
import ProductsInfiniteScroll from '@widgets/products-infinite-scroll'


import { URL_PAGE_CATALOG } from '@shared/contants'
import { Product, ProductsPreviewCardQuery, ProductsPreviewCardQueryVariables, useProductsPreviewCardQuery } from '@shared/generated'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

const LIMIT = 12

export default function NewProductsPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { shopId } = useAddressStore()

  // Keeps the scope a landing's "Новинки" block linked here with.
  const { search } = useLocation()
  const { categoryId, filter } = readProductsScope(search)

  const { data, loading, fetchMore } = useProductsPreviewCardQuery({
    variables: {
      shopId: shopId,
      categoryId,
      filter,
      sort: {
        fieldName: 'new',
      },
    },
  })

  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      ProductsPreviewCardQuery,
      ProductsPreviewCardQueryVariables
    >({
      loading: loading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        categoryId,
        filter,
        sort: {
          fieldName: 'new'
        },
        pager: { limit: LIMIT }
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.products?.edges ?? []
        const curr = fetched?.products?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            products: {
              ...cached.products,
              edges: [ ...prev, ...curr ],
            },
          } as ProductsPreviewCardQuery,
        }
      },
    })

  const totalProducts = data?.products?.pageInfo?.total ?? 0

  return (
    <Container>
      <PageGrid>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={[
                {
                  title: tn('catalog'),
                  to: URL_PAGE_CATALOG
                },
                {
                  title: tp('new')
                }
              ]}
            />
            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesBranch className={styles.aside__content} />
                </div>
              </div>
            )}
            <div className={cn(stylesLayout.content)}>
              <div className={styles.root__top}>
                <TitleWithCount
                  className={styles.root__title}
                  title={tp('new')}
                >
                  {totalProducts !== null && (
                    <>
                      {totalProducts}{' '}
                      {getNoun(
                        totalProducts as number,
                        tp('product.1'),
                        tp('product.2'),
                        tp('product.3'),
                      )}
                    </>
                  )}
                </TitleWithCount>
              </div>
              <ProductsInfiniteScroll
                products={data?.products?.edges as Product[]}
                loading={loading}
                fetchNextPage={fetchNextPage}
                hasMore={hasMore}
                loadingMore={loadingMore}
              />
            </div>
          </div>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}